/**
 * High-shelf audit walk order.
 * Each pair is the two ends of one aisle's parallel bays (where the path folds).
 * The walk zigzags inward: primary, mate, then the next facing letters
 * (aisle 1: C, P, D, O, …). Both heights of a bay are visited before the facing bay.
 * Bays outside that span, and aisles with no pair, stay alphabetical.
 * This is warehouse geometry for the high path, not wms.shelf_pairs (long boxes).
 */

interface FacingBayPair {
  readonly primary: string;
  readonly mate: string;
}

export const HIGH_SHELF_BAY_PAIRS: readonly FacingBayPair[] = [
  { primary: '1-C', mate: '1-P' },
  { primary: '2-E', mate: '2-J' },
  { primary: '3-A', mate: '3-F' },
  { primary: '4-A', mate: '4-F' },
  { primary: '5-A', mate: '5-F' },
  { primary: '6-A', mate: '6-L' },
  { primary: '7-D', mate: '7-I' },
];

interface BayAddress {
  readonly aisle: number;
  readonly bay: string;
}

/** `primary` and `mate` are char codes of the two end bays. */
interface AisleEndPair {
  readonly primary: number;
  readonly mate: number;
}

interface HighShelfWalkKey {
  readonly aisle: number;
  /** 0 before the end-pair span, 1 inside the zigzag, 2 after the span. */
  readonly phase: 0 | 1 | 2;
  /** Step inward from the ends. Used only while `phase` is 1. */
  readonly step: number;
  /** 0 = primary side, 1 = mate side. */
  readonly side: 0 | 1;
  readonly bay: string;
  readonly height: number;
}

const BAY_CODE = /^(\d+)-([A-Z])$/;
const SHELF_NAME = /^(\d+)-([A-Z]+)-(\d+)$/;

function requireBay(code: string): BayAddress {
  const match = BAY_CODE.exec(code);
  const aisleRaw = match?.[1];
  const bay = match?.[2];
  if (!aisleRaw || !bay) {
    throw new Error(`Invalid high-shelf bay pair: ${code}`);
  }
  return { aisle: Number(aisleRaw), bay };
}

function buildEndPairs(): ReadonlyMap<number, AisleEndPair> {
  const pairs = new Map<number, AisleEndPair>();
  for (const pair of HIGH_SHELF_BAY_PAIRS) {
    const primary = requireBay(pair.primary);
    const mate = requireBay(pair.mate);
    if (primary.aisle !== mate.aisle) {
      throw new Error(`High-shelf pair ${pair.primary}/${pair.mate} spans aisles`);
    }
    if (primary.bay.length !== 1 || mate.bay.length !== 1) {
      throw new Error(`High-shelf pair ${pair.primary}/${pair.mate} must be single letters`);
    }
    if (primary.bay === mate.bay) {
      throw new Error(`High-shelf pair ${pair.primary}/${pair.mate} is the same bay`);
    }
    if (pairs.has(primary.aisle)) {
      throw new Error(`High-shelf aisle ${primary.aisle} has two end pairs`);
    }
    pairs.set(primary.aisle, {
      primary: primary.bay.charCodeAt(0),
      mate: mate.bay.charCodeAt(0),
    });
  }
  return pairs;
}

const END_PAIRS: ReadonlyMap<number, AisleEndPair> = buildEndPairs();

function zigzagSlot(pair: AisleEndPair, bayCode: number): Pick<HighShelfWalkKey, 'phase' | 'step' | 'side'> {
  const low = Math.min(pair.primary, pair.mate);
  const high = Math.max(pair.primary, pair.mate);
  if (bayCode < low) return { phase: 0, step: 0, side: 0 };
  if (bayCode > high) return { phase: 2, step: 0, side: 0 };

  const towardMate = pair.mate > pair.primary ? 1 : -1;
  const stepsFromPrimary = (bayCode - pair.primary) * towardMate;
  const span = high - low;
  const letterCount = span + 1;
  const facingPairs = Math.floor(letterCount / 2);
  if (stepsFromPrimary < facingPairs) {
    return { phase: 1, step: stepsFromPrimary, side: 0 };
  }
  if (letterCount % 2 === 1 && stepsFromPrimary === facingPairs) {
    return { phase: 1, step: facingPairs, side: 0 };
  }
  return { phase: 1, step: span - stepsFromPrimary, side: 1 };
}

function parseWalkKey(name: string): HighShelfWalkKey | null {
  const match = SHELF_NAME.exec(name.trim().toUpperCase());
  const aisleRaw = match?.[1];
  const bay = match?.[2];
  const heightRaw = match?.[3];
  if (!aisleRaw || !bay || !heightRaw) return null;

  const aisle = Number(aisleRaw);
  const height = Number(heightRaw);
  const pair = END_PAIRS.get(aisle);
  if (!pair || bay.length !== 1) {
    return { aisle, phase: 0, step: 0, side: 0, bay, height };
  }
  const slot = zigzagSlot(pair, bay.charCodeAt(0));
  return { aisle, bay, height, ...slot };
}

/** Natural shelf order used by the admin list. Unparsed high-path names use this too. */
export function compareShelfNamesNaturally(a: string, b: string): number {
  const left = parseShelfName(a);
  const right = parseShelfName(b);
  const length = Math.max(left.length, right.length);
  for (let i = 0; i < length; i += 1) {
    const leftPart = left[i] ?? '';
    const rightPart = right[i] ?? '';
    if (leftPart === rightPart) continue;
    const compared = compareShelfSegment(leftPart, rightPart);
    if (compared !== 0) return compared;
  }
  return 0;
}

function parseShelfName(name: string): Array<string | number> {
  return (name || '').split(/[-\s]+/).map((segment) => {
    const numeric = Number(segment);
    return Number.isFinite(numeric) ? numeric : segment.toLowerCase();
  });
}

function compareShelfSegment(left: string | number, right: string | number): number {
  const leftIsNumber = typeof left === 'number';
  const rightIsNumber = typeof right === 'number';
  if (leftIsNumber && rightIsNumber) return left - right;
  if (leftIsNumber) return -1;
  if (rightIsNumber) return 1;
  if (left < right) return -1;
  if (left > right) return 1;
  return 0;
}

function compareWalkKeys(left: HighShelfWalkKey, right: HighShelfWalkKey): number {
  if (left.aisle !== right.aisle) return left.aisle - right.aisle;
  if (left.phase !== right.phase) return left.phase - right.phase;
  if (left.phase === 1) {
    if (left.step !== right.step) return left.step - right.step;
    if (left.side !== right.side) return left.side - right.side;
  } else if (left.bay !== right.bay) {
    return left.bay < right.bay ? -1 : 1;
  }
  return left.height - right.height;
}

/** High-path order. Names that are not `aisle-bay-height` use natural order. */
export function compareHighShelfAuditPath(a: string, b: string): number {
  const left = parseWalkKey(a);
  const right = parseWalkKey(b);
  if (!left || !right) return compareShelfNamesNaturally(a, b);
  return compareWalkKeys(left, right);
}
